import React, { FormEvent } from 'react';
import { Button, Callout, Card, Flex, Heading, Text, TextArea, TextField } from '@radix-ui/themes';
import { MEMBER_ROLE_OPTIONS, Member, MemberRole } from '../../types/app';
import MaterialIcon from '../common/MaterialIcon';

type MembersTabProps = {
  isAdmin: boolean;
  members: Member[];
  isLoadingMembers: boolean;
  membersError: string;
  memberSuccess: string;
  memberName: string;
  setMemberName: (value: string) => void;
  memberRole: MemberRole;
  setMemberRole: (value: MemberRole) => void;
  memberDescription: string;
  setMemberDescription: (value: string) => void;
  setMemberImage: (file: File | null) => void;
  isSubmittingMember: boolean;
  handleCreateMember: (event: FormEvent<HTMLFormElement>) => void;
  validateImageFile: (file: File) => string | null;
  setMembersError: (value: string) => void;
  editingMemberId: string | null;
  editName: string;
  setEditName: (value: string) => void;
  editRole: MemberRole;
  setEditRole: (value: MemberRole) => void;
  editDescription: string;
  setEditDescription: (value: string) => void;
  setEditImage: (file: File | null) => void;
  isUpdatingMember: boolean;
  isDeletingMember: boolean;
  handleUpdateMember: (member: Member) => Promise<void>;
  cancelEditMember: () => void;
  startEditMember: (member: Member) => void;
  handleDeleteMember: (member: Member) => Promise<void>;
  getRoleLabel: (role: MemberRole) => string;
};

function MembersTab({
  isAdmin,
  members,
  isLoadingMembers,
  membersError,
  memberSuccess,
  memberName,
  setMemberName,
  memberRole,
  setMemberRole,
  memberDescription,
  setMemberDescription,
  setMemberImage,
  isSubmittingMember,
  handleCreateMember,
  validateImageFile,
  setMembersError,
  editingMemberId,
  editName,
  setEditName,
  editRole,
  setEditRole,
  editDescription,
  setEditDescription,
  setEditImage,
  isUpdatingMember,
  isDeletingMember,
  handleUpdateMember,
  cancelEditMember,
  startEditMember,
  handleDeleteMember,
  getRoleLabel,
}: MembersTabProps) {
  return (
    <>
      <Heading size="6" className="font-display text-pine-900">
        <span className="inline-flex items-center gap-2">
          <MaterialIcon name="groups" className="text-[24px] text-pine-700" />
          Medlemmer
        </span>
      </Heading>
      <Text size="2" color="gray" className="mt-2 block">
        Tilføj nye medlemmer med beskrivelse og billede.
      </Text>

      {isAdmin && (
        <Card className="mt-4 rounded-2xl border border-pine-100 p-4">
          <form className="grid gap-3" onSubmit={handleCreateMember}>
          <label htmlFor="memberName" className="text-sm font-semibold text-slate-700">
            Navn
          </label>
          <TextField.Root
            id="memberName"
            value={memberName}
            onChange={(event) => setMemberName(event.target.value)}
            placeholder="Indtast navn"
            required
          />

          <label htmlFor="memberRole" className="text-sm font-semibold text-slate-700">
            Rolle
          </label>
          <select
            id="memberRole"
            value={memberRole}
            onChange={(event) => setMemberRole(event.target.value as MemberRole)}
            className="rounded-md border border-slate-300 bg-white px-3 py-2 text-sm"
          >
            {MEMBER_ROLE_OPTIONS.map((option) => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))}
          </select>

          <label htmlFor="memberDescription" className="text-sm font-semibold text-slate-700">
            Beskrivelse
          </label>
          <TextArea
            id="memberDescription"
            value={memberDescription}
            onChange={(event) => setMemberDescription(event.target.value)}
            placeholder="Skriv en kort beskrivelse"
            required
          />

          <label htmlFor="memberImage" className="text-sm font-semibold text-slate-700">
            Billede (JPG, PNG eller WEBP, maks 20 MB)
          </label>
          <input
            id="memberImage"
            type="file"
            accept="image/jpeg,image/png,image/webp"
            onChange={(event) => {
              const selectedFile = event.target.files?.[0] || null;
              if (!selectedFile) {
                setMemberImage(null);
                return;
              }

              const validationError = validateImageFile(selectedFile);
              if (validationError) {
                setMembersError(validationError);
                event.currentTarget.value = '';
                setMemberImage(null);
                return;
              }

              setMembersError('');
              setMemberImage(selectedFile);
            }}
          />

          <Button
            type="submit"
            disabled={isSubmittingMember}
            variant="solid"
            color="grass"
            highContrast
          >
            <span className="inline-flex items-center gap-1.5">
              <MaterialIcon name={isSubmittingMember ? 'hourglass_top' : 'person_add'} className="text-[18px]" />
              {isSubmittingMember ? 'Gemmer medlem...' : 'Tilføj medlem'}
            </span>
          </Button>
          </form>
        </Card>
      )}

      {membersError && (
        <Callout.Root color="red" variant="soft" className="mt-4">
          <Callout.Text>{membersError}</Callout.Text>
        </Callout.Root>
      )}

      {memberSuccess && (
        <Callout.Root color="grass" variant="soft" className="mt-4">
          <Callout.Text>{memberSuccess}</Callout.Text>
        </Callout.Root>
      )}

      {isLoadingMembers ? (
        <Text size="2" color="gray" className="mt-4 block">
          Henter medlemmer...
        </Text>
      ) : (
        <div className="mt-4 grid gap-3">
          {MEMBER_ROLE_OPTIONS.map((roleOption) => {
            const membersForRole = members.filter((member) => member.rolle === roleOption.value);

            return (
              <div key={roleOption.value} className="grid gap-3">
                <Heading size="4" className="font-display text-pine-900">
                  {roleOption.label}
                </Heading>

                {!membersForRole.length && (
                  <Text size="2" color="gray" className="rounded-2xl border border-slate-200 bg-slate-50 px-4 py-3">
                    Ingen medlemmer i denne gruppe.
                  </Text>
                )}

                {membersForRole.map((member) => (
                  <Card key={member.id} className="grid gap-4 rounded-2xl border border-pine-100 p-4 md:grid-cols-[120px_1fr]">
                    {member.billedeUrl ? (
                      <img
                        src={member.billedeUrl}
                        alt={member.navn}
                        loading="lazy"
                        decoding="async"
                        className="h-[120px] w-[120px] rounded-xl object-cover"
                      />
                    ) : (
                      <div className="h-[120px] w-[120px] rounded-xl bg-slate-100" />
                    )}

                    <div>
                      {editingMemberId === member.id ? (
                        <div className="grid gap-3">
                          <label htmlFor={`edit-name-${member.id}`} className="text-sm font-semibold text-slate-700">
                            Navn
                          </label>
                          <TextField.Root
                            id={`edit-name-${member.id}`}
                            value={editName}
                            onChange={(event) => setEditName(event.target.value)}
                          />

                          <label htmlFor={`edit-role-${member.id}`} className="text-sm font-semibold text-slate-700">
                            Rolle
                          </label>
                          <select
                            id={`edit-role-${member.id}`}
                            value={editRole}
                            onChange={(event) => setEditRole(event.target.value as MemberRole)}
                            className="rounded-md border border-slate-300 bg-white px-3 py-2 text-sm"
                          >
                            {MEMBER_ROLE_OPTIONS.map((option) => (
                              <option key={option.value} value={option.value}>
                                {option.label}
                              </option>
                            ))}
                          </select>

                          <label htmlFor={`edit-description-${member.id}`} className="text-sm font-semibold text-slate-700">
                            Beskrivelse
                          </label>
                          <TextArea
                            id={`edit-description-${member.id}`}
                            value={editDescription}
                            onChange={(event) => setEditDescription(event.target.value)}
                          />

                          <label htmlFor={`edit-image-${member.id}`} className="text-sm font-semibold text-slate-700">
                            Nyt billede (valgfrit)
                          </label>
                          <input
                            id={`edit-image-${member.id}`}
                            type="file"
                            accept="image/jpeg,image/png,image/webp"
                            onChange={(event) => {
                              const selectedFile = event.target.files?.[0] || null;
                              if (!selectedFile) {
                                setEditImage(null);
                                return;
                              }

                              const validationError = validateImageFile(selectedFile);
                              if (validationError) {
                                setMembersError(validationError);
                                event.currentTarget.value = '';
                                setEditImage(null);
                                return;
                              }

                              setMembersError('');
                              setEditImage(selectedFile);
                            }}
                          />

                          <Flex gap="2" wrap="wrap">
                            <Button
                              type="button"
                              onClick={() => void handleUpdateMember(member)}
                              disabled={isUpdatingMember}
                              variant="solid"
                              color="grass"
                              highContrast
                            >
                              <span className="inline-flex items-center gap-1.5">
                                <MaterialIcon name={isUpdatingMember ? 'hourglass_top' : 'save'} className="text-[18px]" />
                                {isUpdatingMember ? 'Gemmer...' : 'Gem ændringer'}
                              </span>
                            </Button>
                            <Button
                              type="button"
                              onClick={cancelEditMember}
                              variant="soft"
                              color="gray"
                              highContrast
                            >
                              <span className="inline-flex items-center gap-1.5">
                                <MaterialIcon name="close" className="text-[18px]" />
                                Annuller
                              </span>
                            </Button>
                          </Flex>
                        </div>
                      ) : (
                        <>
                          <Text size="4" weight="bold" className="text-pine-900">
                            {member.navn}
                          </Text>
                          <Text size="2" color="gray" className="mt-1 block">
                            {getRoleLabel(member.rolle)}
                          </Text>
                          <Text size="2" color="gray" className="mt-2 block whitespace-pre-line">
                            {member.beskrivelse}
                          </Text>

                          <Flex gap="2" className="mt-3" wrap="wrap">
                            <Button
                              type="button"
                              onClick={() => startEditMember(member)}
                              variant="soft"
                              color="gray"
                              highContrast
                            >
                              <span className="inline-flex items-center gap-1.5">
                                <MaterialIcon name="edit" className="text-[18px]" />
                                Redigér
                              </span>
                            </Button>
                            <Button
                              type="button"
                              onClick={() => void handleDeleteMember(member)}
                              disabled={isDeletingMember}
                              variant="solid"
                              color="red"
                              highContrast
                            >
                              <span className="inline-flex items-center gap-1.5">
                                <MaterialIcon name={isDeletingMember ? 'hourglass_top' : 'delete'} className="text-[18px]" />
                                {isDeletingMember ? 'Sletter...' : 'Slet'}
                              </span>
                            </Button>
                          </Flex>
                        </>
                      )}
                    </div>
                  </Card>
                ))}
              </div>
            );
          })}

          {!members.length && (
            <Text size="2" color="gray" className="rounded-2xl border border-slate-200 bg-slate-50 px-4 py-3">
              Ingen medlemmer endnu.
            </Text>
          )}
        </div>
      )}
    </>
  );
}

export default MembersTab;
